/**
 * Octopus - AlertRecipientsEditor (Fase 1000, AD-02/AD-05)
 *
 * Editor de destinatários (telefones WhatsApp + e-mails) POR CATEGORIA da
 * página de alertas do equipamento. Salva via `putAlertConfig` enviando SÓ a
 * seção editada (whitelist $set por seção no backend, 999.4-03) — nunca apaga
 * as outras categorias. Exibe a badge de órfão OBRIGATÓRIA (AD-05) sempre que
 * o canal correspondente está ligado e não há destinatário cadastrado.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { PhoneListInput, validPhoneList } from '@/components/PhoneNumberInput';
import { putAlertConfig } from '@/services/alertConfigApi';
import { severityConfig } from '@/lib/alertSeverity';

const sanitizeEmails = (rawEmails) => (Array.isArray(rawEmails) ? rawEmails : [])
  .map((email) => String(email || '').trim())
  .filter(Boolean);

const orphanBadgeClass = cn(
  severityConfig.critical.bg,
  severityConfig.critical.color,
  severityConfig.critical.border,
);

/**
 * @param {object} props
 * @param {string} props.equipmentId
 * @param {string} props.category - chave da seção em db.equipment_alert_config
 *   (ex.: "engine_notification", "stream_blindness", "gearbox_abuse").
 * @param {{phones?: string[], emails?: string[], enabled?: boolean, min_severity?: string}} props.section
 * @param {boolean} props.notifyWhatsapp - canal WhatsApp ligado para esta categoria.
 * @param {boolean} props.notifyEmail - canal e-mail ligado para esta categoria.
 * @param {() => void} [props.onSaved]
 */
const AlertRecipientsEditor = ({
  equipmentId,
  category,
  section,
  notifyWhatsapp = false,
  notifyEmail = false,
  onSaved,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [phones, setPhones] = useState(section?.phones?.length ? section.phones : ['']);
  const [emailsText, setEmailsText] = useState((section?.emails || []).join(', '));
  const [saving, setSaving] = useState(false);

  // Resync do estado local quando a categoria muda (nova seção montada) ou o
  // GET é refeito pela página após um save bem-sucedido em outra categoria.
  useEffect(() => {
    setPhones(section?.phones?.length ? section.phones : ['']);
    setEmailsText((section?.emails || []).join(', '));
  }, [category, section]);

  const currentPhones = validPhoneList(phones);
  const currentEmails = sanitizeEmails(emailsText.split(','));

  const phoneOrphan = notifyWhatsapp && currentPhones.length === 0;
  const emailOrphan = notifyEmail && currentEmails.length === 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      await putAlertConfig(equipmentId, {
        [category]: {
          phones: currentPhones,
          emails: currentEmails,
        },
      });
      toast.success('Destinatários salvos');
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração de alertas');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {phoneOrphan && (
        <Badge variant="outline" className={cn('text-xs', orphanBadgeClass)}>
          sem destinatário — WhatsApp não será enviado
        </Badge>
      )}
      {emailOrphan && (
        <Badge variant="outline" className={cn('text-xs', orphanBadgeClass)}>
          sem destinatário — e-mail não será enviado
        </Badge>
      )}

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">
          Telefones (WhatsApp) {!isAdmin && <span className="text-yellow-500">(somente admin)</span>}
        </Label>
        <PhoneListInput
          value={phones}
          onChange={setPhones}
          disabled={!isAdmin}
          idPrefix={`recipients-${category}-phones`}
          compact
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor={`recipients-${category}-emails`} className="text-xs text-muted-foreground">
          E-mails {!isAdmin && <span className="text-yellow-500">(somente admin)</span>}
        </Label>
        <Input
          id={`recipients-${category}-emails`}
          value={emailsText}
          onChange={(e) => setEmailsText(e.target.value)}
          placeholder={isAdmin ? 'email1@empresa.com, email2@empresa.com' : 'Somente admin pode editar'}
          disabled={!isAdmin}
          className={cn('h-8 text-sm', !isAdmin && 'opacity-50 cursor-not-allowed')}
        />
      </div>

      {isAdmin ? (
        <Button size="sm" className="h-8 text-sm" onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Salvar destinatários
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">
          Somente administradores podem editar destinatários.
        </p>
      )}
    </div>
  );
};

export default AlertRecipientsEditor;
